import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, Crown, GraduationCap, Compass, CircleUser, Shield, LogOut, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { BottomNav } from "@/components/BottomNav";
import { NotificationBell } from "@/components/NotificationBell";
import { UniversalSearch } from "@/components/UniversalSearch";
import { ProFreeBanner } from "@/components/ProFreeBanner";
import { PaywallNotice } from "@/components/PaywallNotice";

export function Logo({ onClick }) {
  return (
    <Link to="/" onClick={onClick} data-testid="navbar-logo" className="flex items-center gap-2.5 group shrink-0">
      <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground shadow-sm group-hover:rotate-6 transition-transform duration-300">
        <Zap className="h-5 w-5 fill-current" />
      </span>
      <span className="font-heading font-extrabold text-lg tracking-tight">AI Pathshaalaa</span>
    </Link>
  );
}

const PRIMARY = [
  { label: "Learn", path: "/dashboard", icon: GraduationCap, testid: "nav-link-learn",
    match: (p) => p === "/dashboard" || p.startsWith("/lesson") },
  { label: "Explore", path: "/explore", icon: Compass, testid: "nav-link-explore",
    match: (p) => ["/explore", "/tools", "/universe", "/whats-new"].some((x) => p.startsWith(x)) },
  { label: "Me", path: "/me", icon: CircleUser, testid: "nav-link-me",
    match: (p) => ["/me", "/analytics", "/prompts", "/account"].some((x) => p.startsWith(x)) },
];

function PrimaryLink({ item, active, onClick }) {
  const Icon = item.icon;
  return (
    <button
      data-testid={item.testid}
      onClick={onClick}
      className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition-colors duration-200 ${
        active ? "text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"
      }`}
    >
      {active && (
        <motion.span
          layoutId="nav-active-pill"
          className="absolute inset-0 rounded-full bg-primary/10"
          transition={{ type: "spring", stiffness: 420, damping: 34 }}
        />
      )}
      <Icon className="h-4 w-4 relative z-10" />
      <span className="relative z-10">{item.label}</span>
    </button>
  );
}

function GoProPill({ onClick, isPro }) {
  if (isPro) {
    return (
      <span data-testid="nav-pro-badge" className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-semibold">
        <Crown className="h-4 w-4" /> Pro
      </span>
    );
  }
  return (
    <button
      data-testid="nav-cta-go-pro"
      onClick={onClick}
      className="hidden md:inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white text-sm font-semibold tracking-wide bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 shadow-[0_4px_14px_0_rgba(0,85,255,0.35)] hover:shadow-[0_6px_20px_rgba(0,85,255,0.28)] hover:-translate-y-px transition-all duration-200"
    >
      <Crown className="h-4 w-4" strokeWidth={2.5} /> Go Pro
    </button>
  );
}

function AccountMenu({ user, onNavigate, onLogout }) {
  const initials = (user?.name || user?.email || "U").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-avatar-dropdown-trigger"
          className="flex items-center gap-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span className="grid place-items-center h-9 w-9 rounded-full ring-2 ring-border hover:ring-primary/30 transition-all bg-gradient-to-br from-indigo-100 to-pink-100 dark:from-indigo-900 dark:to-slate-800 text-indigo-700 dark:text-indigo-300 font-heading font-bold text-sm">
            {initials}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={10} className="w-64 p-2 rounded-2xl shadow-xl">
        <div className="flex flex-col gap-0.5 px-3 py-2 mb-1">
          <p className="font-heading font-semibold text-sm truncate">{user?.name || "Learner"}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="dropdown-item-me" onClick={() => onNavigate("/me")}
          className="gap-3 px-3 py-2 rounded-lg cursor-pointer">
          <CircleUser className="h-4 w-4 text-primary" /> My space
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="dropdown-item-account" onClick={() => onNavigate("/account")}
          className="gap-3 px-3 py-2 rounded-lg cursor-pointer">
          <Crown className="h-4 w-4 text-primary" /> My subscription
        </DropdownMenuItem>
        {user?.role === "admin" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="dropdown-item-admin" onClick={() => onNavigate("/admin")}
              className="gap-3 px-3 py-2 rounded-lg cursor-pointer">
              <Shield className="h-4 w-4 text-indigo-500" /> Admin Panel
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="dropdown-item-logout"
          onClick={onLogout}
          className="gap-3 px-3 py-2 rounded-lg cursor-pointer text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-500 focus:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 focus:bg-red-50"
        >
          <LogOut className="h-4 w-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLanding = location.pathname === "/";

  const handleLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  const goStart = () => {
    if (!user) return navigate("/quiz");
    if (user.role === "admin" || user.is_subscribed) return navigate(user.has_path ? "/dashboard" : "/quiz");
    // Non-subscribed users always answer the quiz first; the paywall popup appears at the end.
    return navigate("/quiz");
  };
  const goPro = () => navigate("/subscribe");

  return (
    <>
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar-container"
      className="fixed top-0 inset-x-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/60"
    >
      <ProFreeBanner />
      <PaywallNotice />
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Logo />
          {user && (
            <div className="hidden md:flex items-center gap-1">
              {PRIMARY.map((item) => (
                <PrimaryLink key={item.path} item={item} active={item.match(location.pathname)} onClick={() => navigate(item.path)} />
              ))}
            </div>
          )}
          {isLanding && !user && (
            <div className="hidden md:flex items-center gap-7 font-mono text-xs font-bold uppercase tracking-widest">
              <a href="#how-it-works" className="text-muted-foreground hover:text-primary transition-colors">How it works</a>
              <Link to="/paths" className="text-muted-foreground hover:text-primary transition-colors" data-testid="nav-paths-landing">Paths</Link>
              <Link to="/ai-tools" className="text-muted-foreground hover:text-primary transition-colors" data-testid="nav-tools-landing">AI tools</Link>
              <Link to="/pricing" className="text-muted-foreground hover:text-primary transition-colors" data-testid="nav-pricing-landing">Pricing</Link>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <UniversalSearch />
              <GoProPill isPro={user.is_subscribed || user.role === "admin"} onClick={goPro} />
              <NotificationBell />
              <div className="hidden md:block">
                <AccountMenu user={user} onNavigate={navigate} onLogout={handleLogout} />
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" data-testid="nav-login" onClick={() => navigate("/login")}>
                Log in
              </Button>
              <Button size="sm" data-testid="nav-get-started" onClick={goStart} className="bg-primary text-primary-foreground">
                Get started →
              </Button>
            </div>
          )}
        </div>
      </nav>
    </motion.header>
    <BottomNav />
    </>
  );
}
